import { BlockComponent } from '@blocksuite/affine/block-std';
import { html, nothing, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import type { DeepResearchResultBlockModel } from './model';
import { deepResearchResultStyles } from './styles';

/**
 * Loose shapes for the deep-research result fields. Mirrors the
 * backend Zod schema in
 * `packages/backend/server/src/plugins/copilot/orchestration/types/deep-research.ts`
 * and the loose interfaces in `deep-research-mode.handler.ts`. We
 * deliberately don't import backend types into the frontend bundle.
 */
interface BundleSummaryLoose {
  documentCount?: number;
  themes?: string[];
  overallScope?: string;
  dataTypes?: Record<string, number>;
}

interface DocumentAnalysisLoose {
  documentId?: string;
  documentName?: string;
  summary?: string;
  keyFindings?: string[];
  themes?: string[];
  chunks?: string[];
}

interface SearchHit {
  docName: string;
  docId?: string;
  chunk: string;
  score: number;
}

interface CapsuleDocLoose {
  name?: string;
  sha256?: string;
  sizeBytes?: number;
}

interface CapsuleLoose {
  bundleHash?: string;
  documents?: CapsuleDocLoose[];
  themes?: string[];
  createdAt?: string;
  generator?: string;
}

interface DeepResearchResultLoose {
  capsule?: CapsuleLoose;
  methodsParagraphStyle?: string;
  extractionRows?: Array<{ documentName?: string; documentId?: string }>;
  conflicts?: unknown[];
  gaps?: unknown[];
  documentAnalyses?: DocumentAnalysisLoose[];
}

/**
 * Deep-research-result block — Phase C custom BlockSuite block.
 *
 * Companion to `affine:analyze-result`. Renders the interactive
 * affordances markdown can't carry:
 *
 * - **Download bundle (.json)** — emits the capsule manifest as a
 *   browser download. JSON contains every Phase B field
 *   (extractionRows, conflicts, gaps, methodsParagraph, capsule)
 *   so a colleague or paper supplement can re-cite the synthesis.
 * - **Copy bundle hash** — clipboard.
 * - **Regenerate Conflicts / Gaps** — emits a custom event the
 *   doc-writer interprets as a new turn pre-filled with a section-
 *   scoped query (e.g. "Regenerate the Conflicts section excluding
 *   non-peer-reviewed sources, max 10 refs").
 *
 * Header layout matches analyze-result-block: title → kind-badge →
 * spacer → action buttons. Body shows meta-pills + theme chips.
 * Markdown body (extraction grid table, conflicts table, etc.)
 * still renders below this card from `renderDeepResearchMarkdown`.
 */
export class DeepResearchResultBlockComponent extends BlockComponent<DeepResearchResultBlockModel> {
  static override styles = deepResearchResultStyles;

  @state() private accessor toast = '';
  @state() private accessor searchQuery = '';
  @state() private accessor searchHits: SearchHit[] = [];

  /**
   * Per-document tabbed viewer state. `activeDocIndex` is which tab
   * the researcher is reading; `chunkPage` is the current page of
   * 3-chunk slices inside that tab so a 50-chunk document doesn't
   * dump everything into one scroll well.
   */
  @state() private accessor activeDocIndex = 0;
  @state() private accessor chunkPage = 0;
  private static readonly CHUNKS_PER_PAGE = 3;
  private static readonly MAX_DOC_TABS = 10;

  /**
   * Gap-4 — debounce timer for the search input. Recomputes the
   * BM25 index lazily once per search session and reuses it across
   * keystrokes (memo'd via `searchIndexCache`).
   */
  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  /**
   * Gap-7 — keyboard shortcuts for the headline actions.
   * Cmd/Ctrl + D — download bundle
   * Cmd/Ctrl + Shift + H — copy hash (Cmd+H is reserved on Safari/macOS)
   *
   * We listen on the host element rather than `document` so the
   * shortcuts only fire when the block has focus (avoids stealing
   * keyboard input from the chat textarea).
   */
  override connectedCallback(): void {
    super.connectedCallback();
    // BlockComponent renders into light DOM, so `:host` rules from
    // static styles don't apply. Set host display + margin
    // imperatively so the card has proper breathing room from the
    // agent-stages block above it.
    this.style.display = 'block';
    this.style.margin = '24px 0 16px';
    this.tabIndex = 0;
    this.addEventListener('keydown', this.onKeyDown);
  }

  override disconnectedCallback(): void {
    this.removeEventListener('keydown', this.onKeyDown);
    super.disconnectedCallback();
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    const meta = e.metaKey || e.ctrlKey;
    if (!meta) return;
    if (e.key.toLowerCase() === 'd') {
      e.preventDefault();
      this.downloadBundle();
    } else if (e.key.toLowerCase() === 'h' && e.shiftKey) {
      e.preventDefault();
      void this.copyBundleHash();
    } else if (e.key.toLowerCase() === 'f') {
      e.preventDefault();
      const input = this.shadowRoot?.querySelector<HTMLInputElement>(
        'input.bundle-search-input'
      );
      input?.focus();
    }
  };

  // ======== Gap-4 — Bundle Search (BM25 over per-doc chunks) ========

  /**
   * BM25 index built lazily once per result; cached on the block
   * instance. Uses k1=1.5, b=0.75 (textbook defaults). Tokens are
   * lowercased word-boundaries with a tiny stopword list — good
   * enough for "find IC50 < 1 µM" style searches across 10 papers.
   */
  private searchIndexCache: {
    docs: Array<{
      name: string;
      docId?: string;
      chunk: string;
      tokens: string[];
      length: number;
    }>;
    avgLength: number;
    df: Map<string, number>;
    n: number;
  } | null = null;

  private static readonly STOPWORDS = new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'if', 'is', 'are', 'was', 'were',
    'be', 'been', 'being', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
    'by', 'from', 'as', 'this', 'that', 'these', 'those', 'it', 'its',
  ]);

  private tokenize(text: string): string[] {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9µ%<>=.\s-]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length >= 2 && !DeepResearchResultBlockComponent.STOPWORDS.has(t));
  }

  private buildSearchIndex(): void {
    const result = this.parsedResult;
    const analyses = result?.documentAnalyses ?? [];
    const flat: Array<{
      name: string;
      docId?: string;
      chunk: string;
      tokens: string[];
      length: number;
    }> = [];
    for (const a of analyses) {
      const name = a.documentName ?? a.documentId ?? '—';
      const corpora: string[] = [];
      if (a.summary) corpora.push(a.summary);
      if (a.keyFindings && a.keyFindings.length > 0) {
        corpora.push(...a.keyFindings);
      }
      if (a.chunks && a.chunks.length > 0) {
        corpora.push(...a.chunks);
      }
      for (const c of corpora) {
        const tokens = this.tokenize(c);
        if (tokens.length === 0) continue;
        flat.push({
          name,
          docId: a.documentId,
          chunk: c,
          tokens,
          length: tokens.length,
        });
      }
    }
    const df = new Map<string, number>();
    for (const entry of flat) {
      const seen = new Set<string>();
      for (const t of entry.tokens) {
        if (seen.has(t)) continue;
        seen.add(t);
        df.set(t, (df.get(t) ?? 0) + 1);
      }
    }
    const avgLength =
      flat.length === 0
        ? 0
        : flat.reduce((s, e) => s + e.length, 0) / flat.length;
    this.searchIndexCache = {
      docs: flat,
      avgLength,
      df,
      n: flat.length,
    };
  }

  private runSearch(query: string): SearchHit[] {
    if (!query.trim()) return [];
    if (!this.searchIndexCache) this.buildSearchIndex();
    const idx = this.searchIndexCache;
    if (!idx || idx.n === 0) return [];
    const queryTokens = this.tokenize(query);
    if (queryTokens.length === 0) return [];
    const k1 = 1.5;
    const b = 0.75;
    const scored: SearchHit[] = [];
    for (const entry of idx.docs) {
      let score = 0;
      const tf = new Map<string, number>();
      for (const t of entry.tokens) tf.set(t, (tf.get(t) ?? 0) + 1);
      for (const qt of queryTokens) {
        const f = tf.get(qt);
        if (!f) continue;
        const dfQt = idx.df.get(qt) ?? 0;
        // BM25 IDF: log((N - df + 0.5) / (df + 0.5) + 1)
        const idf = Math.log(
          (idx.n - dfQt + 0.5) / (dfQt + 0.5) + 1
        );
        const numerator = f * (k1 + 1);
        const denominator =
          f + k1 * (1 - b + (b * entry.length) / Math.max(idx.avgLength, 1));
        score += idf * (numerator / denominator);
      }
      if (score > 0) {
        scored.push({
          docName: entry.name,
          docId: entry.docId,
          chunk: entry.chunk,
          score,
        });
      }
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 5);
  }

  private onSearchInput = (e: Event): void => {
    const value = (e.target as HTMLInputElement).value;
    this.searchQuery = value;
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.searchHits = this.runSearch(value);
    }, 120);
  };

  /**
   * Highlight each query token in a chunk for visual scanning.
   * Returns plain HTML — Lit handles escaping when we use unsafeHTML
   * isn't available, so we build a TemplateResult tree instead.
   */
  private renderHighlighted(chunk: string, query: string): TemplateResult[] {
    const tokens = this.tokenize(query);
    if (tokens.length === 0) return [html`${chunk}`];
    const escapedTokens = tokens.map(t =>
      t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );
    const re = new RegExp(`(${escapedTokens.join('|')})`, 'gi');
    const parts = chunk.split(re);
    return parts.map((p, i) =>
      i % 2 === 1
        ? html`<mark>${p}</mark>`
        : html`${p}`
    );
  }

  private get parsedResult(): DeepResearchResultLoose | null {
    const raw = this.model.props.deepResearchResultJson;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as DeepResearchResultLoose;
    } catch {
      return null;
    }
  }

  private get parsedSummary(): BundleSummaryLoose | null {
    const raw = this.model.props.bundleSummaryJson;
    if (!raw) return null;
    try {
      return JSON.parse(raw) as BundleSummaryLoose;
    } catch {
      return null;
    }
  }

  private showToast(msg: string): void {
    this.toast = msg;
    setTimeout(() => {
      if (this.toast === msg) this.toast = '';
    }, 1800);
  }

  /**
   * Pack the full result into a Blob → anchor click → download.
   * Filename uses the bundle hash so two downloads from the same
   * session don't collide on disk.
   */
  private downloadBundle = (): void => {
    const result = this.parsedResult;
    const summary = this.parsedSummary;
    if (!result?.capsule) {
      this.showToast('No capsule on this synthesis');
      return;
    }
    const payload = {
      bundleSummary: summary,
      result,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const hashShort = (result.capsule.bundleHash ?? 'bundle').slice(0, 12);
    a.href = url;
    a.download = `deep-research-${hashShort}.curiescious.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    this.showToast('Bundle downloaded');
  };

  private copyBundleHash = async (): Promise<void> => {
    const hash = this.parsedResult?.capsule?.bundleHash;
    if (!hash) {
      this.showToast('No bundle hash to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(hash);
      this.showToast('Bundle hash copied');
    } catch {
      this.showToast('Clipboard blocked');
    }
  };

  /**
   * Section regenerate — dispatches a CustomEvent the doc-writer
   * listens for and translates into a new chat turn. Contract
   * matches the existing `agent-stages-cancel` pattern. Used for
   * both regenerate buttons (conflicts/gaps) and the proactive
   * suggestion chips (faq / spotGaps / hypothesize) per CLAUDE.md
   * §7 line 159.
   */
  private regenerateSection(
    section:
      | 'conflicts'
      | 'gaps'
      | 'synthesis'
      | 'patterns'
      | 'nextSteps'
      | 'faq'
      | 'spotGaps'
      | 'hypothesize'
  ): void {
    this.dispatchEvent(
      new CustomEvent('deep-research-regenerate', {
        detail: { section },
        bubbles: true,
        composed: true,
      })
    );
    this.showToast(`Regenerating ${section}…`);
  }

  /**
   * Gap-7 / CLAUDE.md §7 line 166 — Text-to-speech.
   *
   * Reads the synthesis sections aloud using the browser's built-in
   * `speechSynthesis` API. No third-party voice dependency. Toggles
   * on/off so a second click stops playback.
   */
  private toggleReadAloud = (): void => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      this.showToast('Speech synthesis not supported here');
      return;
    }
    const synth = window.speechSynthesis;
    if (synth.speaking) {
      synth.cancel();
      this.showToast('Stopped reading');
      return;
    }
    // Voice the synthesis chunks we know about. Title + meta first,
    // then any synthesis text the result carries (we don't have it
    // on the block payload — voice the high-signal labels instead).
    const result = this.parsedResult;
    const summary = this.parsedSummary;
    const segments: string[] = [];
    segments.push('Deep research result.');
    if (summary?.documentCount) {
      segments.push(`${summary.documentCount} documents.`);
    }
    if (summary?.themes?.length) {
      segments.push(`Themes: ${summary.themes.slice(0, 5).join(', ')}.`);
    }
    if (result?.extractionRows?.length) {
      segments.push(`${result.extractionRows.length} extraction rows.`);
    }
    if (result?.conflicts?.length) {
      segments.push(`${result.conflicts.length} conflicts identified.`);
    }
    if (result?.gaps?.length) {
      segments.push(`${result.gaps.length} research gaps identified.`);
    }
    const utter = new SpeechSynthesisUtterance(segments.join(' '));
    utter.rate = 1.0;
    utter.pitch = 1.0;
    synth.speak(utter);
    this.showToast('Reading…');
  };

  /**
   * Tabbed per-document viewer — one tab per bundle doc, capped at
   * MAX_DOC_TABS. Each tab shows the doc summary, key findings, and
   * a paginated chunk reader so the researcher can validate any
   * synthesis claim against the underlying source.
   */
  private renderDocumentTabs(
    analyses: DocumentAnalysisLoose[]
  ): TemplateResult | typeof nothing {
    if (!analyses || analyses.length === 0) return nothing;
    const capped = analyses.slice(
      0,
      DeepResearchResultBlockComponent.MAX_DOC_TABS
    );
    const safeIdx = Math.min(this.activeDocIndex, capped.length - 1);
    const active = capped[safeIdx];
    if (!active) return nothing;

    const chunks = active.chunks ?? [];
    const perPage = DeepResearchResultBlockComponent.CHUNKS_PER_PAGE;
    const pageCount = Math.max(1, Math.ceil(chunks.length / perPage));
    const page = Math.min(this.chunkPage, pageCount - 1);
    const pageChunks = chunks.slice(page * perPage, (page + 1) * perPage);

    return html`<div
      class="doc-tabs"
      role="region"
      aria-label="Per-document viewer"
    >
      <div
        class="doc-tabs-strip"
        role="tablist"
        aria-label="Bundle documents"
      >
        ${capped.map((a, i) => {
          const label = a.documentName ?? a.documentId ?? `Doc ${i + 1}`;
          const selected = i === safeIdx;
          return html`<button
            class="doc-tab"
            role="tab"
            aria-selected=${selected ? 'true' : 'false'}
            tabindex=${selected ? 0 : -1}
            title=${label}
            @click=${() => {
              this.activeDocIndex = i;
              this.chunkPage = 0;
            }}
          >${label}</button>`;
        })}
      </div>
      <div class="doc-tab-body" role="tabpanel">
        ${active.summary
          ? html`<div class="doc-tab-summary">${active.summary}</div>`
          : html`<div class="doc-tab-empty">No summary for this document.</div>`}
        ${active.keyFindings && active.keyFindings.length > 0
          ? html`<div class="doc-tab-section-label">Key findings</div>
              <ul class="doc-tab-findings">
                ${active.keyFindings.map(f => html`<li>${f}</li>`)}
              </ul>`
          : nothing}
        ${chunks.length > 0
          ? html`<div class="doc-tab-section-label">
                Excerpts (${chunks.length})
              </div>
              <div class="doc-tab-chunks">
                ${pageChunks.map(
                  (c, i) => html`<div>
                    <strong>#${page * perPage + i + 1}</strong>
                    — ${c}
                  </div>`
                )}
              </div>
              ${pageCount > 1
                ? html`<div class="doc-tab-pager">
                    <button
                      ?disabled=${page === 0}
                      @click=${() => (this.chunkPage = Math.max(0, page - 1))}
                    >
                      ← Prev
                    </button>
                    <span>Page ${page + 1} of ${pageCount}</span>
                    <button
                      ?disabled=${page >= pageCount - 1}
                      @click=${() =>
                        (this.chunkPage = Math.min(pageCount - 1, page + 1))}
                    >
                      Next →
                    </button>
                  </div>`
                : nothing}`
          : nothing}
      </div>
    </div>`;
  }

  private renderMeta(
    summary: BundleSummaryLoose | null,
    result: DeepResearchResultLoose | null
  ): TemplateResult {
    const items: Array<[string, string]> = [];
    if (summary?.documentCount !== undefined) {
      items.push([
        'Docs',
        `${summary.documentCount}`,
      ]);
    }
    if (summary?.dataTypes) {
      const breakdown = Object.entries(summary.dataTypes)
        .map(([k, v]) => `${v} ${k}`)
        .join(', ');
      if (breakdown) items.push(['Types', breakdown]);
    }
    if (result?.extractionRows && result.extractionRows.length > 0) {
      items.push(['Grid', `${result.extractionRows.length} rows`]);
    }
    if (result?.conflicts && result.conflicts.length > 0) {
      items.push(['Conflicts', `${result.conflicts.length}`]);
    }
    if (result?.gaps && result.gaps.length > 0) {
      items.push(['Gaps', `${result.gaps.length}`]);
    }
    if (result?.capsule?.bundleHash) {
      items.push([
        'Hash',
        result.capsule.bundleHash.slice(0, 8),
      ]);
    }
    if (result?.methodsParagraphStyle) {
      items.push(['Methods', result.methodsParagraphStyle]);
    }
    if (items.length === 0) return html`${nothing}`;
    return html`<div class="meta">
      ${items.map(
        ([k, v]) => html`<span class="meta-item"
          ><span class="label">${k}</span
          ><span class="value">${v}</span></span
        >`
      )}
    </div>`;
  }

  override renderBlock(): TemplateResult {
    const result = this.parsedResult;
    const summary = this.parsedSummary;
    if (!result && !summary) {
      return html`<div class="card">
        <div class="body">
          <em>(No deep-research result on this block.)</em>
        </div>
      </div>`;
    }
    const hasCapsule = !!result?.capsule;
    const hasConflicts = !!result?.conflicts && result.conflicts.length > 0;
    const hasGaps = !!result?.gaps && result.gaps.length > 0;
    const docCount = summary?.documentCount ?? 0;
    const titleLabel = `Deep Research result${docCount ? ` · ${docCount} docs` : ''}`;
    const themes = summary?.themes ?? [];
    const dominantTheme = themes[0];
    // Truncate long sentence-style themes for the badge — full text
    // surfaces on hover via the title attribute.
    const truncate = (s: string, max = 32): string =>
      s.length > max ? s.slice(0, max - 1).trimEnd() + '…' : s;
    const badgeLabel = dominantTheme
      ? truncate(dominantTheme.split(/[:\.]/)[0].trim() || dominantTheme)
      : 'deep-research';
    const kindBadge = html`<span
      class="kind-badge"
      title=${dominantTheme ?? 'deep-research'}
    >${badgeLabel}</span>`;
    return html`<div
      class="card"
      role="region"
      aria-label="Deep research result"
    >
      <div class="header">
        <div class="title-row">
          <div class="title">${titleLabel}</div>
          ${kindBadge}
        </div>
        <div class="actions" role="toolbar" aria-label="Result actions">
          <button
            class="btn"
            data-disabled=${hasCapsule ? 'false' : 'true'}
            ?disabled=${!hasCapsule}
            @click=${this.downloadBundle}
            title="Download the bundle as JSON for paper supplement / collaborator hand-off"
            aria-label="Download bundle as JSON file"
            aria-keyshortcuts="Meta+D"
          >
            ⇩ Download bundle
          </button>
          <button
            class="btn"
            data-disabled=${hasCapsule ? 'false' : 'true'}
            ?disabled=${!hasCapsule}
            @click=${this.copyBundleHash}
            title="Copy the bundle SHA-256 for citation"
            aria-label="Copy bundle hash to clipboard"
            aria-keyshortcuts="Meta+H"
          >
            ⧉ Copy hash
          </button>
          <button
            class="btn"
            data-disabled=${hasConflicts ? 'false' : 'true'}
            ?disabled=${!hasConflicts}
            @click=${() => this.regenerateSection('conflicts')}
            title="Regenerate the Conflicts section with peer-reviewed-only sources"
            aria-label="Regenerate conflicts section with peer-reviewed sources only"
          >
            ↻ Conflicts
          </button>
          <button
            class="btn"
            data-disabled=${hasGaps ? 'false' : 'true'}
            ?disabled=${!hasGaps}
            @click=${() => this.regenerateSection('gaps')}
            title="Regenerate the Gaps section with refined PubMed queries"
            aria-label="Regenerate gaps section with refined PubMed queries"
          >
            ↻ Gaps
          </button>
          <button
            class="btn"
            @click=${this.toggleReadAloud}
            title="Read the bundle summary aloud"
            aria-label="Read result summary aloud"
          >
            🔊 Read
          </button>
        </div>
      </div>
      ${this.renderMeta(summary, result)}
      <!--
        P0-4 — per-document tabbed viewer. One tab per bundle doc
        (capped at 10) so researchers can validate any claim in the
        synthesis against the underlying source without leaving the
        result card.
      -->
      ${result?.documentAnalyses
        ? this.renderDocumentTabs(result.documentAnalyses)
        : nothing}
      <!--
        Gap-4 / CLAUDE.md §7 line 161 — Bundle Search bar.
        Searches across every uploaded document's summary, findings,
        and parsed text. Fully client-side BM25 — no network call,
        instant results. Cmd/Ctrl+F focuses the input.
      -->
      ${result?.documentAnalyses && result.documentAnalyses.length > 0
        ? html`<div
            class="bundle-search"
            role="search"
            aria-label="Search across uploaded documents"
          >
            <input
              type="search"
              class="bundle-search-input"
              placeholder="Search the bundle… (Cmd/Ctrl+F)"
              .value=${this.searchQuery}
              @input=${this.onSearchInput}
              aria-label="Search across uploaded documents"
            />
            ${this.searchHits.length > 0
              ? html`<ul class="bundle-search-results" role="list">
                  ${this.searchHits.map(
                    h => html`<li>
                      <span class="bundle-search-doc">${h.docName}</span>
                      <span class="bundle-search-snippet"
                        >${this.renderHighlighted(
                          h.chunk.slice(0, 240) +
                            (h.chunk.length > 240 ? '…' : ''),
                          this.searchQuery
                        )}</span
                      >
                    </li>`
                  )}
                </ul>`
              : this.searchQuery
                ? html`<div class="bundle-search-empty">
                    No matches in this bundle.
                  </div>`
                : nothing}
          </div>`
        : nothing}
      <!--
        Gap-5 / CLAUDE.md §7 line 159 — proactive suggestion chips.
        Section label sits above so the row reads as a callout, not
        a continuation of the meta pills.
      -->
      <div class="section-label">Suggested actions</div>
      <div class="theme-row" role="group" aria-label="Suggested actions">
        <button
          class="theme-chip"
          @click=${() => this.regenerateSection('faq')}
          title="Generate an FAQ from this bundle"
          aria-label="Generate frequently asked questions from this bundle"
        >
          ❓ Generate FAQ
        </button>
        <button
          class="theme-chip"
          @click=${() => this.regenerateSection('spotGaps')}
          title="Identify research gaps with draft PubMed queries"
          aria-label="Identify research gaps with draft PubMed queries"
        >
          🔍 Spot Gaps
        </button>
        <button
          class="theme-chip"
          @click=${() => this.regenerateSection('hypothesize')}
          title="Hypothesize 3 testable outcomes implied by this synthesis"
          aria-label="Hypothesize testable outcomes implied by this synthesis"
        >
          💡 Hypothesize Outcomes
        </button>
      </div>
      ${themes.length > 1
        ? html`<div class="section-label">Other themes</div>
          <div class="theme-row">
            ${themes.slice(1, 6).map(
              t => html`<span class="theme-chip" title=${t}
                >${truncate(t.split(/[:\.]/)[0].trim() || t, 36)}</span
              >`
            )}
          </div>`
        : nothing}
      ${this.toast
        ? html`<div class="toast">${this.toast}</div>`
        : nothing}
    </div>`;
  }
}
